import { List, Map, OrderedSet, Record } from 'immutable'
import { combineActions, handleActions } from 'redux-actions'

import * as actions from './actions'
import { Activity, CATEGORY_TAG_DEFAULT } from 'store/records/activity'
import listMove from 'helpers/immutable/listMove'
import * as generatorActions from 'store/entities/generator/actions'
import { syncValidateForLibraryActivity, syncValidateForMicroActionActivity } from 'app/validators/activities/create'
import * as galleryActions from 'store/entities/gallery/actions'
import { DEFAULT_ACTIVITY_IMAGE_URL } from 'app/constants/gallery'

export const Activities = Record({
  all: Map(),
  view: null,
  ids: OrderedSet(),
  dirty: false,
  loaded: false,
  presenter: null,
  beforeCreateOrder: null,
  touched: false,
  categoriesTags: List(),
})

const handleGetActivitiesRequest = state => {
  return state.set('loaded', false)
}

const handleGetActivitiesSuccess = (state, { payload: { data } }) => {
  let ids = OrderedSet()

  return state
    .set(
      'all',
      data.reduce((acc, activity) => {
        ids = ids.add(activity.id)
        if (!activity.categoryTag) {
          activity.categoryTag = CATEGORY_TAG_DEFAULT
        }
        return acc.set(activity.id.toString(), new Activity(activity))
      }, Map())
    )
    .set('ids', ids)
    .set('loaded', true)
    .set('touched', false)
}

const handlePostActivitySuccess = (state, { payload: { data: activity }, meta: { tempId } }) => {
  return state
    .set('ids', state.get('ids').add(activity.id).delete(tempId))
    .setIn(
      ['all', activity.id.toString()],
      new Activity({
        ...activity,
        invalid: false,
        categoryTag: activity.categoryTag ? activity.categoryTag : CATEGORY_TAG_DEFAULT,
      })
    )
    .deleteIn(['all', tempId])
}

const handlePutActivitySuccess = (state, { payload: { data: activity } }) => {
  return state.setIn(
    ['all', activity.id.toString()],
    new Activity({
      ...activity,
      invalid: false,
      categoryTag: activity.categoryTag ? activity.categoryTag : CATEGORY_TAG_DEFAULT,
    })
  )
}

const handleSetView = (state, { payload }) => {
  return state.set('view', payload.id.toString())
}

const handleSetOrder = (state, { payload }) => {
  const { list, oldIndex, newIndex } = payload
  const newList = listMove(list, oldIndex, newIndex).map((activity, index) => activity.set('day', index + 1))
  return state
    .set(
      'all',
      newList.reduce((acc, el) => acc.set(el.get('id').toString(), el), Map())
    )
    .set('touched', true)
}

const handleSetBeforeCreateOrder = (state, { payload }) => {
  return state.set('beforeCreateOrder', payload.day)
}

const handleSetDeleted = (state, { payload }) => {
  return state.setIn(['all', payload.id.toString(), 'deleting'], payload.isDeleting).set('touched', true)
}

const handleSetPinned = (state, { payload }) => {
  return state.setIn(['all', payload.id.toString(), 'pinned'], payload.isPinned).set('touched', true)
}

const handleAddActivitiesFromFile = (state, { payload }) => {
  let ids = OrderedSet()
  const { data: activitiesArray } = payload
  const all = activitiesArray.reduce((acc, activity, index) => {
    const activityId = `temp-${index}-${activity.id}`
    ids = ids.add(activityId)
    return acc.set(activityId.toString(), new Activity({ ...activity, id: activityId, day: index + 1 }))
  }, Map())
  return state.set('all', all).set('ids', ids).set('touched', true)
}

const handleDeleteTempActivity = (state, { payload }) => {
  const ids = state.get('ids').delete(payload.id)
  return state.deleteIn(['all', payload.id.toString()]).set('ids', ids)
}

const handleChangeActivityExampleSuccess = (state, { payload: { data } }) => {
  return state.setIn(['all', data.id.toString(), 'example'], data.example)
}

const handleUpdateActivityImage = (state, { payload: { id, image } }) => {
  return state.setIn(['all', id, 'image'], image).setIn(['all', id, 'isImageLinkInvalid'], false)
}

const handleFetchCategoriesTagsForProTrackLevel = (state, { payload: { data } }) => {
  const handledCategoriesTags = data.map(tag => {
    const categoryTag = {
      ...tag,
      label: tag.name,
    }
    return categoryTag
  })

  return state.set('categoriesTags', List(handledCategoriesTags))
}

// used when submit changes for activity from generator or replace one activity from generator to another regular activity
const handleReplaceActivity = (state, { payload }) => {
  const ids = state.get('ids').delete(payload.replacedActivityId).toJS()
  const replacedActivity = state.getIn(['all', payload.replacedActivityId.toString()])
  const replacedActivityDay = replacedActivity.get('day')
  // newActivityId must be random because when we try to replace on activity which already exists
  const newActivityId = replacedActivity.get('id')
  const newIds = OrderedSet(List([newActivityId, ...ids]))
  let activityForReplace
  if (payload.isFromFile) {
    activityForReplace = payload.activityForReplace
      .set('id', newActivityId)
      .set('day', replacedActivityDay)
      .set('invalid', false)
      .set('isImageLinkInvalid', false)
  } else {
    activityForReplace = payload.activityForReplace.set('id', newActivityId).set('day', replacedActivityDay)
  }
  return state.setIn(['all', newActivityId], new Activity(activityForReplace)).set('ids', newIds).set('touched', true)
}

const handleReplaceOldImageLinkInActivities = (state, { payload }) => {
  const { oldImagePath, imagePath } = payload

  const updatedActivities = state.get('all').map(activity => {
    const currentImagePath = activity.get('image')
    if (currentImagePath === oldImagePath) {
      return activity.set('image', imagePath)
    }
    return activity
  })

  return state.set('all', updatedActivities)
}

export const handleTriggerProtrackActivitiesValidation = (state, { payload }) => {
  const { isLibraryLevel } = payload
  const validator = isLibraryLevel ? syncValidateForLibraryActivity : syncValidateForMicroActionActivity
  /*
    validator returns object with errors or undefined if validation passed succsesfully
  */
  const activitiesList = state.get('all').map(activity => {
    if (!validator(activity)) {
      return activity.set('invalid', false)
    }
    return activity
  })
  return state.set('all', activitiesList)
}

const handleDeleteImageFromGallery = (state, { payload }) => {
  const activities = state.get('all').reduce((acc, activity) => {
    if (activity.get('image') === payload.url) {
      return acc.set(activity.get('id').toString(), activity.set('image', DEFAULT_ACTIVITY_IMAGE_URL))
    }
    return acc.set(activity.get('id').toString(), activity)
  }, Map())
  return state.set('all', activities)
}

export default handleActions(
  {
    [actions.getActivities]: handleGetActivitiesRequest,
    [combineActions(actions.getActivities.success, actions.postActivitiesListBulk.success)]: handleGetActivitiesSuccess,
    [actions.postActivity.success]: handlePostActivitySuccess,
    [actions.putActivity.success]: handlePutActivitySuccess,
    [actions.setView]: handleSetView,
    [actions.setOrder]: handleSetOrder,
    [actions.setBeforeCreateOrder]: handleSetBeforeCreateOrder,
    [actions.setDeleted]: handleSetDeleted,
    [actions.setPinned]: handleSetPinned,
    [actions.addActivitiesFromFile]: handleAddActivitiesFromFile,
    [actions.deleteTempActivity]: handleDeleteTempActivity,
    [actions.fetchCategoriesTagsForProTrackLevel.success]: handleFetchCategoriesTagsForProTrackLevel,
    [combineActions(actions.addActivityToExamples.success, actions.removeActivityFromExamples.success)]:
      handleChangeActivityExampleSuccess,
    [actions.updateActivityImage]: handleUpdateActivityImage,
    [generatorActions.replaceActivity]: handleReplaceActivity,
    [actions.replaceOldImageLinkInActivities]: handleReplaceOldImageLinkInActivities,
    [actions.triggerProtrackLevelActivitiesValidation]: handleTriggerProtrackActivitiesValidation,
    [galleryActions.deleteImageFromGallery.success]: handleDeleteImageFromGallery,
  },
  new Activities()
)
